import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiX, FiDownload, FiTrash2, FiLoader, FiUploadCloud, FiInfo, FiChevronUp, FiChevronDown, FiRotateCcw, FiImage, FiSliders, FiStar, FiMaximize, FiEye, FiCheck, FiZap } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { useJobHistory, formatSeconds, formatSize } from "./audioShared";

const MAX_FILE_BYTES = 25 * 1024 * 1024;
const ADJUSTMENTS = [
  ["brightness", "Brightness", -100, 100], ["contrast", "Contrast", -100, 100], ["saturation", "Colour", -100, 100], ["warmth", "Warmth (cool - warm)", -100, 100], ["sharpness", "Sharpness", 0, 100],
];
const NO_ADJUSTMENT = { brightness: 0, contrast: 0, saturation: 0, warmth: 0, sharpness: 0 };
const TABS = [["looks", "Looks", FiStar], ["effects", "Effects", FiImage], ["adjust", "Adjust", FiSliders], ["export", "Size & download", FiMaximize]];

const cardClass = "rounded-2xl bg-white/5 border border-navy-border p-5";
const labelClass = "text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3";
const ghost = "px-3.5 py-2 rounded-full border border-white/20 hover:border-accent-light text-xs font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-50";

function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

async function download(url, filename) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("download failed");
    saveBlob(filename, await response.blob());
  } catch {
    window.open(url, "_blank", "noopener");
  }
}

const baseName = (name) => (name || "picture").replace(/\.[^.]+$/, "").replace(/[^A-Za-z0-9_-]+/g, "-").slice(0, 50) || "picture";

function Slider({ label, value, min = 0, max = 100, onChange, suffix = "" }) {
  return (
    <label className="block">
      <span className="flex items-center justify-between text-xs text-gray-300 mb-1.5">{label}<span className="text-gray-500 tabular-nums">{value}{suffix}</span></span>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#22d3ee]" aria-label={label} />
    </label>
  );
}

// AI Image Upscaling and Enhancement: upload a picture, try glass, grey, shine and many more effects live, make it bigger with AI,
// and download it. Effects run on our server; only the optional AI upscaling / face steps use an outside AI service.
export default function ImageEnhancerTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("imagefx");
  const [options, setOptions] = useState(null);
  const [picture, setPicture] = useState(null);            // { token, width, height, name, image, gallery }
  const [stack, setStack] = useState([]);                  // [{ effect, amount }]
  const [adjust, setAdjust] = useState(NO_ADJUSTMENT);
  const [tab, setTab] = useState("looks");
  const [preview, setPreview] = useState({ src: "", state: "idle", message: "" });
  const [split, setSplit] = useState(0);                   // 0 = only the edited picture, 100 = only the original
  const [holding, setHolding] = useState(false);
  const [scale, setScale] = useState(1);
  const [upscale, setUpscale] = useState("auto");
  const [faces, setFaces] = useState(false);
  const [format, setFormat] = useState("png");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const inputRef = useRef(null);
  const resultRef = useRef(null);
  const requestId = useRef(0);

  const effectInfo = useCallback((key) => options?.groups.flatMap((g) => g.effects).find((e) => e.key === key), [options]);
  const query = `client_id=${encodeURIComponent(clientId)}`;

  useEffect(() => {
    let alive = true;
    apiRequest("/core/imagefx/options/").then(({ ok, data }) => { if (alive && ok) setOptions(data); });
    return () => { alive = false; };
  }, []);

  // The full stack sent to the server: the effects, then the sliders.
  const payload = useCallback(() => {
    const items = stack.map((s) => ({ effect: s.effect, amount: s.amount }));
    if (Object.values(adjust).some(Boolean)) items.unshift({ effect: "adjust", ...adjust });
    return items;
  }, [stack, adjust]);

  // Live preview, drawn on the server a moment after the last change.
  useEffect(() => {
    if (!picture) return undefined;
    const items = payload();
    if (!items.length) { setPreview({ src: picture.image, state: "ready", message: "" }); return undefined; }
    const id = ++requestId.current;
    setPreview((p) => ({ ...p, state: "loading" }));
    const timer = setTimeout(async () => {
      const { ok, data } = await apiRequest("/core/imagefx/preview/", { method: "POST", body: { token: picture.token, stack: items } });
      if (id !== requestId.current) return;                 // a newer change has already been asked for
      if (!ok) return setPreview((p) => ({ ...p, state: "error", message: data?.detail || errorMessage(data) }));
      setPreview({ src: data.image, state: "ready", message: "", width: data.width, height: data.height });
    }, 280);
    return () => clearTimeout(timer);
  }, [picture, payload]);

  // Show the newest finished picture automatically.
  useEffect(() => {
    if (latest && latest.status === "completed" && latest.images?.length) setViewId(latest.id);
  }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?${query}`).then(({ ok, data }) => { if (alive) setDetail(ok ? data : null); });
    return () => { alive = false; };
  }, [viewId, query]);

  const upload = async (file) => {
    setFieldError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) return setFieldError("Choose a JPG, PNG or WebP picture.");
    if (file.size > MAX_FILE_BYTES) return setFieldError("This picture is larger than 25 MB.");
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    const { ok, data } = await apiRequest("/core/imagefx/upload/", { method: "POST", body: form });
    setUploading(false);
    if (!ok) return setFieldError(data?.file || data?.detail || errorMessage(data));
    setPicture(data);
    setStack([]);
    setAdjust(NO_ADJUSTMENT);
    setSplit(0);
    setTab("looks");
    setPreview({ src: data.image, state: "ready", message: "" });
  };

  const toggleEffect = (key) => {
    setStack((current) => {
      if (current.some((s) => s.effect === key)) return current.filter((s) => s.effect !== key);
      if (current.length >= (options?.max_effects || 8)) { setError(`You can use at most ${options?.max_effects || 8} effects at once. Remove one first.`); return current; }
      setError("");
      return [...current, { effect: key, amount: effectInfo(key)?.amount ?? 60 }];
    });
  };
  const setAmount = (key, amount) => setStack((current) => current.map((s) => (s.effect === key ? { ...s, amount } : s)));
  const move = (index, delta) => setStack((current) => {
    const next = [...current];
    const target = index + delta;
    if (target < 0 || target >= next.length) return current;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const applyLook = (look) => { setError(""); setStack(look.stack.map((s) => ({ ...s }))); setAdjust(NO_ADJUSTMENT); };
  const reset = () => { setStack([]); setAdjust(NO_ADJUSTMENT); setError(""); };

  const create = async () => {
    setError("");
    setCreating(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "imagefx");
    form.append("token", picture.token);
    form.append("name", picture.name);
    form.append("stack", JSON.stringify(payload()));
    form.append("scale", String(scale));
    form.append("upscale", upscale);
    form.append("faces", faces ? "true" : "false");
    form.append("format", format);
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setCreating(false);
    if (!ok) return setError(data?.stack || data?.token || errorMessage(data));
    setViewId(null);
    add(data);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const hasChanges = payload().length > 0 || scale > 1 || faces;
  const shownSrc = holding || split >= 100 ? picture?.image : preview.src;
  const comparable = preview.width && picture && Math.abs(preview.width / preview.height - picture.width / picture.height) < 0.01;
  const longSide = picture ? Math.max(picture.width, picture.height) : 0;
  const aiLimit = (s) => options?.ai_limits?.[String(s)] || 0;
  const outSize = picture ? [picture.width * scale, picture.height * scale] : [0, 0];

  const view = detail && detail.status === "completed" ? detail : null;
  const info = view?.segments && !Array.isArray(view.segments) ? view.segments : {};

  // =====================================================================================================
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Image Upscaling and Enhancement</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-10">
        Upload a picture, then give it glass, grey, shining, cinematic, sketch and many other looks, with a live preview. Make it 2 or 4 times bigger with an AI upscaler,
        restore faces, and download the result.
      </p>

      {!picture ? (
        <div className={cardClass}>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
            role="button" tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-16 text-center transition-colors ${dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"}`}
          >
            {uploading ? <FiLoader className="mx-auto text-accent-light animate-spin" size={34} /> : <FiUploadCloud className="mx-auto text-accent-light" size={34} />}
            <p className="mt-3 text-base text-gray-100">{uploading ? "Opening your picture..." : "Click to choose a picture, or drop it here"}</p>
            <p className="text-xs text-gray-500 mt-1">JPG, PNG or WebP - up to 25 MB</p>
          </div>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          {fieldError && <p className="text-sm text-amber-300 mt-4">{fieldError}</p>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 text-sm">
            {[["Glass", "Frosted glass, glass panel, wavy and fluted glass, glass tiles, glossy reflection."], ["Grey & colour", "Black and white, dramatic grey, silver, sepia, cinematic, vintage."],
              ["Shine & glow", "Glow, sparkle, sweeping shine, golden hour, light leaks, neon edges."], ["Art & frames", "Pencil sketch, cartoon, oil painting, watercolor, pixel art, polaroid."]].map(([title, text]) => (
              <div key={title} className="rounded-xl bg-navy border border-navy-border p-4"><p className="font-semibold mb-1">{title}</p><p className="text-xs text-gray-400 leading-relaxed">{text}</p></div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-6">
          {/* ----------------------------------------------------------------- preview and stack */}
          <div className="space-y-4">
            <div className={cardClass}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-sm font-semibold truncate max-w-[60%]" title={picture.name}>{picture.name} <span className="text-xs text-gray-500 font-normal">{picture.width}×{picture.height}</span></p>
                <div className="flex items-center gap-2">
                  <button type="button" onMouseDown={() => setHolding(true)} onMouseUp={() => setHolding(false)} onMouseLeave={() => setHolding(false)}
                    onTouchStart={() => setHolding(true)} onTouchEnd={() => setHolding(false)} className={ghost}><FiEye size={13} /> Hold to see original</button>
                  <button type="button" onClick={() => { setPicture(null); setStack([]); setAdjust(NO_ADJUSTMENT); }} className={ghost}><FiX size={13} /> New picture</button>
                </div>
              </div>
              <div className="relative rounded-xl overflow-hidden bg-[repeating-conic-gradient(#1b2338_0%_25%,#141a2b_0%_50%)] bg-[length:20px_20px] flex items-center justify-center min-h-[240px]">
                {shownSrc && <img src={shownSrc} alt="Live preview with your effects" className="max-h-[68vh] w-auto max-w-full object-contain" />}
                {comparable && split > 0 && split < 100 && preview.src && (
                  <img src={picture.image} alt="Your original" className="absolute max-h-[68vh] w-auto max-w-full object-contain pointer-events-none" style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }} />
                )}
                {preview.state === "loading" && <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 text-xs text-white inline-flex items-center gap-1.5"><FiLoader className="animate-spin" size={12} /> Updating</span>}
                {(holding || split >= 100) && <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-xs text-white">Original</span>}
              </div>
              {comparable && (
                <label className="block mt-3">
                  <span className="flex justify-between text-[11px] text-gray-500 mb-1"><span>Original</span><span>Edited</span></span>
                  <input type="range" min={0} max={100} value={100 - split} onChange={(e) => setSplit(100 - Number(e.target.value))} className="w-full accent-[#22d3ee]" aria-label="Compare original and edited" />
                </label>
              )}
              {preview.state === "error" && <p className="text-sm text-amber-300 mt-3">{preview.message}</p>}
              <p className="text-xs text-gray-500 mt-3">The preview is a reduced copy; the final picture is made at full size.</p>
            </div>

            <div className={cardClass}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Your effects ({stack.length})</span>
                {(stack.length > 0 || Object.values(adjust).some(Boolean)) && <button type="button" onClick={reset} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1"><FiRotateCcw size={12} /> Clear all</button>}
              </div>
              {stack.length === 0 ? (
                <p className="text-sm text-gray-500">Choose a look or click effects on the right. They are applied in the order shown, and you can mix as many as you like.</p>
              ) : (
                <ul className="space-y-3">
                  {stack.map((item, i) => (
                    <li key={item.effect} className="rounded-xl bg-navy border border-navy-border p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-sm font-medium">{effectInfo(item.effect)?.label || item.effect}</span>
                        <span className="flex items-center gap-1">
                          <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Apply earlier" className="p-1 text-gray-400 hover:text-white disabled:opacity-30"><FiChevronUp size={15} /></button>
                          <button type="button" onClick={() => move(i, 1)} disabled={i === stack.length - 1} aria-label="Apply later" className="p-1 text-gray-400 hover:text-white disabled:opacity-30"><FiChevronDown size={15} /></button>
                          <button type="button" onClick={() => toggleEffect(item.effect)} aria-label={`Remove ${effectInfo(item.effect)?.label || item.effect}`} className="p-1 text-gray-400 hover:text-red-400"><FiX size={15} /></button>
                        </span>
                      </div>
                      <Slider label="Strength" value={item.amount} min={1} max={100} onChange={(v) => setAmount(item.effect, v)} suffix="%" />
                    </li>
                  ))}
                </ul>
              )}
              {error && <p className="text-sm text-amber-300 mt-3">{error}</p>}
            </div>
          </div>

          {/* ----------------------------------------------------------------- the choices */}
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {TABS.map(([key, label, Icon]) => (
                <button key={key} type="button" onClick={() => setTab(key)} aria-pressed={tab === key}
                  className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${tab === key ? "bg-brand-gradient text-white shadow-md shadow-accent/20" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"}`}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>

            {tab === "looks" && (
              <div className="grid sm:grid-cols-2 gap-3">
                {(options?.presets || []).map((look) => (
                  <button key={look.key} type="button" onClick={() => applyLook(look)}
                    className="text-start rounded-xl bg-white/5 border border-navy-border hover:border-accent-light overflow-hidden transition-colors">
                    {picture.gallery[look.stack[0].effect] && <img src={picture.gallery[look.stack[0].effect]} alt="" className="w-full h-24 object-cover" loading="lazy" />}
                    <div className="p-3"><p className="text-sm font-semibold">{look.label}</p><p className="text-xs text-gray-400 mt-0.5">{look.description}</p></div>
                  </button>
                ))}
              </div>
            )}

            {tab === "effects" && (
              <div className="space-y-6">
                {(options?.groups || []).map((group) => (
                  <div key={group.name}>
                    <span className={labelClass}>{group.name}</span>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                      {group.effects.map((effect) => {
                        const on = stack.some((s) => s.effect === effect.key);
                        return (
                          <button key={effect.key} type="button" onClick={() => toggleEffect(effect.key)} aria-pressed={on} title={effect.description}
                            className={`rounded-lg overflow-hidden border text-start transition-colors ${on ? "border-accent-light ring-2 ring-accent-light/50" : "border-navy-border hover:border-accent-light/70"}`}>
                            <span className="block relative">
                              {picture.gallery[effect.key] ? <img src={picture.gallery[effect.key]} alt="" className="w-full aspect-square object-cover" loading="lazy" /> : <span className="block w-full aspect-square bg-navy" />}
                              {on && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-accent-light text-navy inline-flex items-center justify-center"><FiCheck size={12} /></span>}
                            </span>
                            <span className="block px-1.5 py-1.5 text-[11px] leading-tight text-gray-200 bg-navy">{effect.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "adjust" && (
              <div className={cardClass}>
                <div className="space-y-5">
                  {ADJUSTMENTS.map(([key, label, min, max]) => <Slider key={key} label={label} value={adjust[key]} min={min} max={max} onChange={(v) => setAdjust((a) => ({ ...a, [key]: v }))} />)}
                </div>
                <button type="button" onClick={() => setAdjust(NO_ADJUSTMENT)} className={`${ghost} mt-5`}><FiRotateCcw size={13} /> Reset sliders</button>
              </div>
            )}

            {tab === "export" && (
              <div className={`${cardClass} space-y-6`}>
                <div>
                  <span className={labelClass}>Size</span>
                  <div className="grid grid-cols-3 gap-2.5">
                    {[[1, "Original"], [2, "2× bigger"], [4, "4× bigger"]].map(([value, label]) => (
                      <button key={value} type="button" onClick={() => setScale(value)} aria-pressed={scale === value}
                        className={`rounded-xl border p-3 text-start transition-colors ${scale === value ? "border-accent-light bg-accent/10" : "border-navy-border bg-white/5 hover:border-accent-light/60"}`}>
                        <p className="text-sm font-semibold">{label}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">{picture.width * value}×{picture.height * value}</p>
                      </button>
                    ))}
                  </div>
                  {scale > 1 && (
                    <div className="mt-3 text-xs leading-relaxed">
                      {longSide <= aiLimit(scale) ? (
                        <p className="text-emerald-300 inline-flex items-start gap-1.5"><FiZap className="mt-0.5 shrink-0" size={12} /> The AI upscaler (Real-ESRGAN) can do this one: it redraws fine detail instead of just stretching.</p>
                      ) : (
                        <p className="text-amber-200 inline-flex items-start gap-1.5"><FiInfo className="mt-0.5 shrink-0" size={12} /> This picture is too large for the free AI upscaler at {scale}× (limit {aiLimit(scale)} px on the long side), so a careful smooth upscale is used.</p>
                      )}
                      {options && outSize[0] > options.max_output_pixels && <p className="text-amber-200 mt-1">The result is limited to {options.max_output_pixels} px on the long side.</p>}
                    </div>
                  )}
                  {scale > 1 && (
                    <label className="block mt-4">
                      <span className="text-xs text-gray-400 block mb-1.5">Method</span>
                      <select value={upscale} onChange={(e) => setUpscale(e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg bg-navy border border-navy-border text-sm text-white">
                        <option value="auto">AI upscaler when possible (best)</option>
                        <option value="smooth">Smooth upscale only (instant, nothing sent out)</option>
                      </select>
                    </label>
                  )}
                </div>

                <label className="flex items-start gap-3 text-sm text-gray-200 cursor-pointer">
                  <input type="checkbox" checked={faces} onChange={(e) => setFaces(e.target.checked)} className="mt-1 accent-[#22d3ee] w-4 h-4" />
                  <span>Restore faces <span className="block text-xs text-gray-500">Sharpens and repairs faces in old, blurry or low-quality photos (GFPGAN AI).</span></span>
                </label>

                <div>
                  <span className={labelClass}>File type</span>
                  <div className="flex gap-2">
                    {[["png", "PNG (best quality)"], ["jpg", "JPG (smaller file)"]].map(([value, label]) => (
                      <button key={value} type="button" onClick={() => setFormat(value)} aria-pressed={format === value}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${format === value ? "bg-accent-light text-navy border-accent-light" : "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light"}`}>{label}</button>
                    ))}
                  </div>
                </div>

                <button type="button" onClick={create} disabled={creating || !!running || !hasChanges}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
                  {creating ? <><FiLoader className="animate-spin" /> Sending...</> : running ? <><FiLoader className="animate-spin" /> Working...</> : <><FiMaximize /> Create final picture</>}
                </button>
                {!hasChanges && <p className="text-xs text-gray-500 text-center -mt-3">Choose an effect, a bigger size or face restoration first.</p>}
                <p className="text-xs text-gray-500">Effects are made on our server and your upload is deleted after a few hours. Only the AI upscaling and face steps send your picture to a free outside AI service (AI Horde).</p>
              </div>
            )}

            {tab !== "export" && hasChanges && (
              <button type="button" onClick={() => setTab("export")} className="mt-5 w-full px-5 py-3 rounded-full bg-brand-gradient text-white text-sm font-semibold shadow-md shadow-accent/20">Next: size and download</button>
            )}
          </div>
        </div>
      )}

      <div ref={resultRef} />
      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div>
            <p className="font-semibold">Making your picture... {formatSeconds(elapsed)}{running.progress && <span className="text-gray-400 font-normal"> · {running.progress}</span>}</p>
            <p className="text-sm text-gray-400">Effects take a few seconds; the AI upscaler can add up to a minute or two when its free servers are busy.</p>
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" /><span>{latest.error_message || "The picture could not be made. Please try again."}</span>
        </div>
      )}

      {view && !running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">Your finished picture</p>
              <p className="font-semibold">{view.original_name}</p>
              <p className="text-xs text-gray-400 mt-1">
                {info.original_size && `${info.original_size[0]}×${info.original_size[1]} → `}{info.size && `${info.size[0]}×${info.size[1]} px`}{info.bytes ? ` · ${formatSize(info.bytes)}` : ""}
                {info.effects?.length > 0 && ` · ${info.effects.join(", ")}`}
              </p>
            </div>
            {view.images[0] && (
              <button type="button" onClick={() => download(view.images[0].url, `${baseName(view.original_name)}-enhanced.${info.format || "png"}`)}
                className="px-5 py-2.5 rounded-full bg-brand-gradient text-white text-sm font-semibold inline-flex items-center gap-2 shadow-md shadow-accent/20"><FiDownload /> Download {(info.format || "png").toUpperCase()}</button>
            )}
          </div>
          {info.notes?.length > 0 && (
            <ul className="text-xs text-gray-300 bg-navy border border-navy-border rounded-xl px-4 py-3 mb-4 space-y-1">{info.notes.map((n) => <li key={n}>· {n}</li>)}</ul>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            {view.source_image && (
              <figure className="rounded-xl overflow-hidden bg-navy border border-navy-border">
                <img src={view.source_image} alt="Original upload" className="w-full object-contain bg-black/30 max-h-[520px]" />
                <figcaption className="px-4 py-2.5 text-xs text-gray-400">Original</figcaption>
              </figure>
            )}
            {view.images[0] && (
              <figure className="rounded-xl overflow-hidden bg-navy border border-navy-border">
                <a href={view.images[0].url} target="_blank" rel="noreferrer"><img src={view.images[0].url} alt="Finished result" className="w-full object-contain bg-black/30 max-h-[520px]" /></a>
                <figcaption className="px-4 py-2.5 text-xs text-gray-400">Result (click to open at full size)</figcaption>
              </figure>
            )}
          </div>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your finished pictures</p>
        {loading ? <p className="text-sm text-gray-400">Loading...</p> : history.length === 0 ? <p className="text-sm text-gray-500">Nothing here yet. Finished pictures will appear here.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border overflow-hidden ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                {job.status === "completed" && job.images?.length ? (
                  <button type="button" onClick={() => setViewId(job.id)} className="block w-full" aria-label={`Show ${job.original_name}`}><img src={job.images[0].thumb} alt={job.original_name} className="w-full aspect-square object-cover" loading="lazy" /></button>
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center text-xs text-gray-500 px-3 text-center bg-navy">
                    {job.status === "failed" ? <span className="text-red-300">{job.error_message}</span> : <span className="inline-flex items-center gap-2"><FiLoader className="animate-spin" /> Working...</span>}
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs text-gray-200 truncate" title={job.original_name}>{job.original_name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-gray-500">{job.detected_language || new Date(job.created_at).toLocaleDateString()}</span>
                    {job.status !== "processing" && job.status !== "queued" && (
                      <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400"><FiTrash2 size={14} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
